import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, Volume2 } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { APP_LINKS, APP_VIDEO } from "@/data/content";
import { scrollToId } from "@/lib/lenis";

// Captures réelles de l'application — ratio d'origine : 920 x 2000
const SCREEN_RATIO = "46 / 100";

function PhoneFrame({ src, alt, children, className = "", testid }) {
  return (
    <div
      className={`relative overflow-hidden rounded-[2.6rem] border border-gold/25 bg-night-soft p-1.5 shadow-[0_30px_80px_rgba(0,0,0,0.6)] ${className}`}
      data-testid={testid}
    >
      <div className="relative w-full overflow-hidden rounded-[2.1rem] bg-background" style={{ aspectRatio: SCREEN_RATIO }}>
        {src ? (
          <img src={src} alt={alt} className="h-full w-full object-cover" loading="lazy" />
        ) : (
          children
        )}
      </div>
      <span className="pointer-events-none absolute inset-0 rounded-[2.6rem] ring-1 ring-inset ring-white/10" aria-hidden="true" />
    </div>
  );
}

function CyclingPhone({ images, className = "", testid }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), 4200);
    return () => clearInterval(t);
  }, [images.length]);

  return (
    <PhoneFrame className={className} testid={testid}>
      {images.map((im, i) => (
        <img
          key={im.src}
          src={im.src}
          alt={im.alt}
          aria-hidden={i !== idx}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`}
          loading="lazy"
        />
      ))}
      <div className="absolute bottom-2.5 left-1/2 z-10 flex -translate-x-1/2 gap-1.5">
        {images.map((_, i) => (
          <span key={i} className={`h-1.5 w-1.5 rounded-full transition-colors duration-300 ${i === idx ? "bg-gold" : "bg-white/30"}`} />
        ))}
      </div>
    </PhoneFrame>
  );
}

function VideoFrame() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
  };

  return (
    <div
      className="relative w-[290px] overflow-hidden rounded-[2.6rem] border border-gold/25 bg-night-soft p-1.5 shadow-[0_30px_80px_rgba(0,0,0,0.6)] sm:w-[330px]"
      data-testid="app-video-frame"
    >
      <video
        ref={videoRef}
        loop
        playsInline
        preload="none"
        poster="/video/presentation-poster.jpg"
        onClick={toggle}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        data-testid="app-presentation-video"
        className="block w-full cursor-pointer rounded-[2.1rem]"
        style={{ aspectRatio: "720 / 1260" }}
      >
        <source src="/video/presentation.mp4" type='video/mp4; codecs="avc1.42E01E, mp4a.40.2"' />
        <source src="/video/presentation.webm" type='video/webm; codecs="vp9, opus"' />
      </video>
      {!playing && (
        <button
          type="button"
          onClick={toggle}
          data-testid="app-video-play-button"
          aria-label="Lire la vidéo de présentation avec le son"
          className="group absolute inset-0 z-10 flex items-center justify-center rounded-[2.6rem] bg-night/35 backdrop-blur-[2px]"
        >
          <span className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-gradient-to-r from-gold-deep via-gold to-gold-hover text-night shadow-[0_10px_40px_rgba(212,175,55,0.5)] transition-transform duration-300 group-hover:scale-110">
            <Play className="ml-1 h-8 w-8" fill="currentColor" strokeWidth={0} />
          </span>
        </button>
      )}
      <span className="pointer-events-none absolute inset-0 rounded-[2.6rem] ring-1 ring-inset ring-white/10" aria-hidden="true" />
      <div className="absolute bottom-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-night/70 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/85 backdrop-blur-md" data-testid="app-video-sound-badge">
        {playing ? "Lecture — son activé" : "Cliquez pour lancer avec le son"}
      </div>
    </div>
  );
}

const SCREENS = {
  actus: { src: "/app-screens/actus.webp", alt: "Fil d'actualité de l'application MBC84" },
  annuaire: { src: "/app-screens/annuaire.webp", alt: "Annuaire des membres avec recherche et filtres" },
  agenda: { src: "/app-screens/agenda.webp", alt: "Agenda des événements du club" },
  event: { src: "/app-screens/event.webp", alt: "Détail d'un événement avec inscription" },
  forum: { src: "/app-screens/forum.webp", alt: "Forum d'échanges entre membres" },
};

export default function AppMobile() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yLeft = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const yRight = useTransform(scrollYProgress, [0, 1], [90, -40]);

  return (
    <section id="application" ref={ref} className="relative overflow-hidden bg-night-soft py-16 md:py-24 lg:py-32" data-testid="app-section">
      <div
        className="pointer-events-none absolute left-1/2 top-0 h-[600px] w-[900px] -translate-x-1/2 opacity-50"
        style={{ background: "radial-gradient(ellipse, rgba(56,189,248,0.07) 0%, rgba(212,175,55,0.06) 45%, transparent 70%)" }}
        aria-hidden="true"
      />
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-8 lg:px-8">
        <div>
          <SectionHead
            eyebrow="L'application MBC84"
            title={<span>Tout le club <span className="text-gold-grad">dans votre poche.</span></span>}
            sub="L'application Motards Business Club 84 permet aux membres de rester connectés, de développer leur réseau, de découvrir les événements et de retrouver toute la communauté où qu'ils soient."
          />
          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a href={APP_LINKS.appStore} data-testid="app-appstore-button" className="btn-sheen inline-flex items-center justify-center gap-3 rounded-xl bg-foreground px-5 py-3 transition-transform duration-300 hover:scale-[1.03]" style={{ color: "hsl(var(--background))" }}>
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6"><path d="M16.7 12.9c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.8-3.5.8-.7 0-1.9-.8-3.1-.8-1.6 0-3.1.9-3.9 2.4-1.7 2.9-.4 7.2 1.2 9.5.8 1.1 1.7 2.4 3 2.4 1.2-.1 1.7-.8 3.1-.8s1.9.8 3.1.8c1.3 0 2.1-1.2 2.9-2.3.9-1.3 1.3-2.6 1.3-2.7-.1 0-2.7-1-2.7-4zM14.4 5.9c.6-.8 1.1-1.9 1-3-1 0-2.1.6-2.8 1.5-.6.7-1.2 1.9-1 3 1.1 0 2.2-.7 2.8-1.5z" /></svg>
                <span className="flex flex-col text-left leading-tight">
                  <span className="text-[10px] uppercase tracking-widest opacity-70">Télécharger sur</span>
                  <span className="text-sm font-bold">l'App Store</span>
                </span>
              </a>
              <a href={APP_LINKS.googlePlay} data-testid="app-googleplay-button" className="btn-sheen inline-flex items-center justify-center gap-3 rounded-xl border border-gold/40 bg-transparent px-5 py-3 text-foreground transition-all duration-300 hover:border-gold hover:bg-gold/10">
                <svg viewBox="0 0 24 24" className="h-6 w-6"><path d="M4 3.5v17c0 .4.5.7.8.4l9.1-8.5c.2-.2.2-.6 0-.8L4.8 3.1c-.3-.3-.8 0-.8.4z" fill="#D4AF37" /><path d="M14 8.3l3.5-2c.5-.3 1.1.1 1.1.6v10.2c0 .6-.6.9-1.1.6l-3.5-2" fill="#38BDF8" /></svg>
                <span className="flex flex-col text-left leading-tight">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Disponible sur</span>
                  <span className="text-sm font-bold">Google Play</span>
                </span>
              </a>
            </div>
            <button
              type="button"
              onClick={() => scrollToId("fonctionnalites")}
              data-testid="app-discover-button"
              className="group mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-gold"
            >
              Découvrir l'application
              <span className="h-px w-8 bg-gold transition-all duration-300 group-hover:w-14" />
            </button>
          </Reveal>
        </div>

        <div className="relative flex items-end justify-center gap-4 lg:gap-6">
          <motion.div style={{ y: yLeft }} className="hidden w-[204px] md:block lg:w-[224px]">
            <CyclingPhone
              testid="app-mockup-left"
              images={[SCREENS.annuaire, SCREENS.forum]}
              className="rotate-[-5deg]"
            />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} className="w-[250px] lg:w-[276px]">
            <PhoneFrame testid="app-mockup-feed" src={SCREENS.actus.src} alt={SCREENS.actus.alt} className="z-10" />
          </motion.div>
          <motion.div style={{ y: yRight }} className="hidden w-[204px] md:block lg:w-[224px]">
            <CyclingPhone
              testid="app-mockup-right"
              images={[SCREENS.agenda, SCREENS.event]}
              className="rotate-[5deg]"
            />
          </motion.div>
        </div>

        <Reveal delay={0.1} className="mt-6 lg:col-span-2">
          <div className="flex flex-col items-center gap-6">
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-gold/70" />
              <span className="eyebrow">La présentation en vidéo</span>
              <span className="h-px w-10 bg-gold/70" />
            </div>
            <VideoFrame />
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Volume2 className="h-3.5 w-3.5 text-gold" />
              La visite guidée de l'application MBC84, avec le son.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
